import { findLayer, findLayerParent, type EditorDocument, type GroupLayer, type Layer } from '../core/model';

export interface IsolationScope {
  layerId: string;
  objectId?: string;
}

export function layerInIsolation(document: EditorDocument, layerId: string, scope: IsolationScope | null): boolean {
  if (!scope) return true;
  if (scope.objectId) return layerId === scope.layerId;
  let current = findLayer(document.root, layerId);
  while (current) {
    if (current.id === scope.layerId) return true;
    const location = findLayerParent(document.root, current.id);
    if (!location || location.parent === document.root) return false;
    current = location.parent;
  }
  return false;
}

function isolatedBranch(layer: Layer, scope: IsolationScope): Layer | null {
  if (layer.id === scope.layerId) {
    if (!scope.objectId) return layer;
    if (layer.kind !== 'vector') return null;
    const object = layer.vector.objects.find((candidate) => candidate.id === scope.objectId);
    return object ? { ...layer, vector: { ...layer.vector, objects: [object] } } : null;
  }
  if (layer.kind !== 'group') return null;
  const children = layer.children.flatMap((child) => {
    const branch = isolatedBranch(child, scope);
    return branch ? [branch] : [];
  });
  return children.length ? { ...layer, children } : null;
}

/** Shallow isolation view: pixel stores and object geometry remain shared and read-only. */
export function isolationDocument(document: EditorDocument, scope: IsolationScope): EditorDocument | null {
  if (!findLayer(document.root, scope.layerId)) return null;
  const root: GroupLayer = {
    ...document.root,
    children: document.root.children.flatMap((layer) => {
      const branch = isolatedBranch(layer, scope);
      return branch ? [branch] : [];
    }),
  };
  return root.children.length ? { ...document, root } : null;
}
